import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import ReactDatePicker from 'react-datepicker';
import { format, isValid, parseISO } from 'date-fns';

import translations from 'services/translations/translations';
import useDateLocale from 'services/translations/useDateLocale';

import { getOpenToDate } from './DatePicker';

import 'style/layout/form/Datepicker.css';

// Tables and windows clip everything outside them, so the calendar is drawn on the page
const CalendarOnTop = ({
  children,
}: {
  children: React.ReactNode[];
}): React.ReactPortal => ReactDOM.createPortal(children, document.body);

const toDate = (value: null | string | undefined): Date | null => {
  if (!value) {
    return null;
  }
  const date = parseISO(value);
  return isValid(date) ? date : null;
};

/**
 * A day of the form yyyy-mm-dd chosen from the calendar of the application, e.g. the from
 * and to of a search. An empty field opens the calendar at today, the end of a period
 * (rangeStart given) a month after its start.
 */
const DateInput = ({
  ariaLabel,
  className,
  disabled = false,
  id,
  isClearable = true,
  onChange,
  placeholder,
  rangeStart,
  title,
  value,
}: {
  ariaLabel?: string;
  className?: string;
  // Shown but not changed, e.g. without the right to change it
  disabled?: boolean;
  id?: string;
  isClearable?: boolean;
  // The day as yyyy-mm-dd, empty when cleared
  onChange: (value: string) => void;
  placeholder?: string;
  // The start of the period this field ends (yyyy-mm-dd)
  rangeStart?: null | string;
  title?: string;
  value: string;
}): JSX.Element => {
  const dateLocale = useDateLocale();
  const { t } = useTranslation([translations.common]);
  const selected = toDate(value);

  return (
    <div className="date-picker date-input" title={title}>
      <ReactDatePicker
        ariaLabelledBy={ariaLabel}
        calendarClassName="with-week-numbers"
        className={`form-control ${className ?? ''}`}
        dateFormat={dateLocale.dateFormat}
        disabled={disabled}
        id={id}
        isClearable={isClearable && !disabled}
        locale={dateLocale.datePicker}
        onChange={(date) =>
          onChange(date instanceof Date ? format(date, 'yyyy-MM-dd') : '')
        }
        openToDate={getOpenToDate(selected, toDate(rangeStart))}
        placeholderText={placeholder}
        popperContainer={CalendarOnTop}
        selected={selected}
        showWeekNumbers
        weekLabel={t(`${translations.common}:Week short`)}
      />
    </div>
  );
};

export default DateInput;
